import { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { M365User, AvailableLicense, getLicenseDisplayName } from '../../types/m365Users'

interface AssignLicenseModalProps {
  user: M365User
  customerId: string
  customerTenantId: string
  onClose: () => void
  onLicenseUpdated: () => void
}

export function AssignLicenseModal({ user, customerId, customerTenantId, onClose, onLicenseUpdated }: AssignLicenseModalProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [loading, setLoading] = useState(false)
  const [loadingLicenses, setLoadingLicenses] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [availableLicenses, setAvailableLicenses] = useState<AvailableLicense[]>([])
  const [selectedLicenses, setSelectedLicenses] = useState<Set<string>>(new Set())

  // Initialize selected licenses from user's current assignments
  useEffect(() => {
    if (user.assignedLicenses) {
      setSelectedLicenses(new Set(user.assignedLicenses.map(l => l.skuId)))
    }
  }, [user.assignedLicenses])

  // Fetch available licenses
  useEffect(() => {
    const fetchLicenses = async () => {
      try {
        // Use customer tenant token (same as M365UsersTab)
        const token = await getCustomerGraphAccessToken(customerTenantId)
        const response = await api.getM365AvailableLicenses(token, customerId)
        // Filter to only show licenses that can be assigned to users
        const userLicenses = response.licenses.filter((l: AvailableLicense) => l.appliesTo === 'User')
        setAvailableLicenses(userLicenses)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load available licenses')
      } finally {
        setLoadingLicenses(false)
      }
    }
    fetchLicenses()
  }, [customerId, customerTenantId, getCustomerGraphAccessToken])

  const handleToggleLicense = (skuId: string) => {
    setSelectedLicenses(prev => {
      const newSet = new Set(prev)
      if (newSet.has(skuId)) {
        newSet.delete(skuId)
      } else {
        newSet.add(skuId)
      }
      return newSet
    })
  }

  const handleSave = async () => {
    setLoading(true)
    setError(null)

    try {
      // Use customer tenant token for license assignment
      const token = await getCustomerGraphAccessToken(customerTenantId)

      // Calculate what to add and remove
      const currentLicenses = new Set(user.assignedLicenses?.map(l => l.skuId) || [])
      const addLicenses: Array<{ skuId: string }> = []
      const removeLicenses: string[] = []

      // Find licenses to add
      selectedLicenses.forEach(skuId => {
        if (!currentLicenses.has(skuId)) {
          addLicenses.push({ skuId })
        }
      })

      // Find licenses to remove
      currentLicenses.forEach(skuId => {
        if (!selectedLicenses.has(skuId)) {
          removeLicenses.push(skuId)
        }
      })

      // Only call API if there are changes
      if (addLicenses.length === 0 && removeLicenses.length === 0) {
        onClose()
        return
      }

      await api.assignM365UserLicense(token, customerId, user.id, {
        addLicenses,
        removeLicenses,
      })

      onLicenseUpdated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update licenses')
    } finally {
      setLoading(false)
    }
  }

  const currentLicenses = new Set(user.assignedLicenses?.map(l => l.skuId) || [])
  const hasChanges = () => {
    if (selectedLicenses.size !== currentLicenses.size) return true
    for (const skuId of selectedLicenses) {
      if (!currentLicenses.has(skuId)) return true
    }
    return false
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block w-full max-w-lg my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg sm:align-middle">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Manage Licenses</h3>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {/* User info */}
            <div className="flex items-center mb-4 pb-4 border-b border-gray-200">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-medium ${user.accountEnabled ? 'bg-primary-500' : 'bg-gray-400'}`}>
                {user.displayName?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                <p className="text-sm text-gray-500">{user.userPrincipalName}</p>
              </div>
            </div>

            {/* Usage location warning */}
            {!user.usageLocation && (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex">
                  <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-yellow-700">
                    <strong>Usage location not set.</strong> You must set a usage location before assigning licenses.
                    Edit the user first to set their usage location.
                  </p>
                </div>
              </div>
            )}

            {/* License list */}
            {loadingLicenses ? (
              <div className="flex items-center justify-center py-8">
                <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                <span className="ml-2 text-gray-600">Loading available licenses...</span>
              </div>
            ) : availableLicenses.length === 0 ? (
              <div className="text-center py-8">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                <p className="mt-2 text-sm text-gray-500">No licenses available in this tenant</p>
              </div>
            ) : (
              <div className="space-y-2">
                {availableLicenses.map((license) => {
                  const isSelected = selectedLicenses.has(license.skuId)
                  const isCurrentlyAssigned = currentLicenses.has(license.skuId)
                  const isAvailable = license.availableUnits > 0 || isCurrentlyAssigned
                  const disabled = !user.usageLocation || (!isAvailable && !isSelected)

                  return (
                    <label
                      key={license.skuId}
                      className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                        disabled
                          ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                          : isSelected
                          ? 'bg-blue-50 border-blue-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleLicense(license.skuId)}
                        disabled={disabled}
                        className="h-4 w-4 mt-1 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:cursor-not-allowed"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {getLicenseDisplayName(license.skuPartNumber)}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            license.availableUnits > 0
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {license.availableUnits} available
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{license.skuPartNumber}</p>
                        <div className="mt-1 flex items-center text-xs text-gray-400">
                          <span>{license.consumedUnits} / {license.totalUnits} used</span>
                          {isCurrentlyAssigned && (
                            <span className="ml-2 text-blue-600">(Currently assigned)</span>
                          )}
                        </div>
                      </div>
                    </label>
                  )
                })}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {selectedLicenses.size} license{selectedLicenses.size !== 1 ? 's' : ''} selected
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading || !hasChanges() || !user.usageLocation}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Saving...
                  </span>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
